"use client";

import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef, useEffect } from "react";
import { remark } from "remark";
import strip from "strip-markdown";
import { usePromptCopy } from "@/hooks/usePromptCopy";

interface CopyPromptButtonProps {
  content: string;
  promptId: string;
  label?: string; // Optional label for the button
  variant?: "outline" | "default"; // To support different visual styles if needed
}

export function CopyPromptButton({ 
  content, 
  promptId,
  label = "Copy", // Default to "Copy"
  variant = "outline" // Default to outline style from PromptCard
}: CopyPromptButtonProps) {
  const plainTextRef = useRef<string>("");
  const copyToClipboard = usePromptCopy(promptId);

  useEffect(() => {
    // Convert markdown to plain text when component mounts
    const convertToPlainText = async () => {
      try {
        const result = await remark()
          .use(strip)
          .process(content);
        plainTextRef.current = String(result);
      } catch (error) {
        console.error("Error converting markdown to plain text:", error);
        plainTextRef.current = content; // Fallback to original content
      }
    };
    
    convertToPlainText();
  }, [content]);

  const handleCopy = () => {
    const textToCopy = plainTextRef.current || content;
    copyToClipboard(textToCopy);
  };

  // Choose styling based on the variant
  const buttonStyle = variant === "outline" 
    ? "rounded-[50px] border-[#A4AFB7] hover:bg-transparent hover:border-zinc-900 px-[30px] py-[20px]"
    : "rounded-[50px] bg-[#B8EF52] text-zinc-900 hover:bg-[#a5d649] px-6 md:px-8 h-[44px] md:h-[48px] lg:h-[52px] flex items-center gap-2 font-normal text-[14px] md:text-[15px] lg:text-[16px]";

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={handleCopy}
      className={buttonStyle}
    >
      <Copy className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}